import type { EvaluacionTernaDTO } from '@/services/requisitos.service';
import type { TernaDTO } from '@/services/sysadmin.service';
import { persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

// ========================================
// Types
// ========================================

/** Per-terna change: observaciones, planMejora, fechaImplementacion, evidenciaIds (NO calificación) */
export interface CambioTerna {
  observaciones?: string;
  planMejora?: string;
  fechaImplementacion?: string;
  evidenciaIds?: number[];
}

const emptyCambio: CambioTerna = {
  observaciones: '',
  planMejora: '',
  fechaImplementacion: '',
  evidenciaIds: [],
};

// ========================================
// Helpers
// ========================================

/**
 * Compute evaluation key for a terna:
 * - Grouped ternas: "grupo:{grupo}"
 * - Ungrouped ternas: "terna:{id}"
 */
export function getEvalKey(terna: { id: number; grupo: string | null }): string {
  return terna.grupo ? `grupo:${terna.grupo}` : `terna:${terna.id}`;
}

function formatDateForInput(dateString?: string | null): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

function esIgualCampo<T>(a: T, b: T): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

// ========================================
// Store
// ========================================

interface EvaluacionTernaState {
  cambios: Record<string, CambioTerna>;
  valoresOriginales: Record<string, CambioTerna>;
  valoresAnteriores: Record<string, CambioTerna>; // baseline before this asesoría started
  /** Maps evalKey → representative ternaId (for PATCH requests) */
  evalKeyToTernaId: Record<string, number>;
  asesoriaId: string | null;
  inicializado: boolean;

  initOriginales: (asesoriaId: string, evaluacionesTernas: EvaluacionTernaDTO[], ternas: TernaDTO[]) => void;
  initAnteriores: (anteriorMap: Record<string, CambioTerna>) => void;
  setCampo: <K extends keyof CambioTerna>(evalKey: string, campo: K, valor: CambioTerna[K]) => void;
  limpiarCambios: () => void;
  limpiarCambiosEspecificos: (evalKeys: string[]) => void;
  resetStore: () => void;
  getValor: <K extends keyof CambioTerna>(evalKey: string, campo: K) => CambioTerna[K] | undefined;
  getValores: (evalKey: string) => CambioTerna;
}

const store = createWithEqualityFn<EvaluacionTernaState>()(
  persist(
    (set, get) => ({
      cambios: {},
      valoresOriginales: {},
      valoresAnteriores: {},
      evalKeyToTernaId: {},
      asesoriaId: null,
      inicializado: false,

      initOriginales: (asesoriaId, evaluacionesTernas, ternas) => {
        set((state) => {
          // Build evalKey → representative ternaId mapping and ternaId → evalKey mapping
          const evalKeyToTernaId: Record<string, number> = {};
          const ternaIdToEvalKey: Record<number, string> = {};
          for (const terna of ternas) {
            const key = getEvalKey(terna);
            if (!(key in evalKeyToTernaId)) {
              evalKeyToTernaId[key] = terna.id;
            }
            ternaIdToEvalKey[terna.id] = key;
          }

          // Initialize all eval keys with defaults
          const valoresOriginales: Record<string, CambioTerna> = {};
          for (const terna of ternas) {
            const key = getEvalKey(terna);
            if (!(key in valoresOriginales)) {
              valoresOriginales[key] = { ...emptyCambio };
            }
          }

          // Fill in values from existing evaluations
          for (const ev of evaluacionesTernas) {
            let key: string | null = null;

            // Si la evaluación trae ternaId, usamos la llave ACTUAL de esa terna según el Layout.
            // Si no, recaemos en la llave que traiga la evaluación por compatibilidad hacia atrás.
            if (ev.ternaId != null && ev.ternaId in ternaIdToEvalKey) {
              key = ternaIdToEvalKey[ev.ternaId];
            } else if (ev.grupo) {
              key = `grupo:${ev.grupo}`;
            } else if (ev.ternaId != null) {
              key = `terna:${ev.ternaId}`;
            }

            if (key) {
              valoresOriginales[key] = {
                observaciones: ev.observaciones ?? '',
                planMejora: ev.planMejora ?? '',
                fechaImplementacion: formatDateForInput(ev.fechaImplementacion),
                evidenciaIds: ev.evidenciaIds ?? [],
              };
            }
          }

          return {
            valoresOriginales,
            evalKeyToTernaId,
            cambios: state.asesoriaId === asesoriaId ? state.cambios : {},
            asesoriaId,
            inicializado: true,
          };
        });
      },

      resetStore: () => {
        set({
          cambios: {},
          valoresOriginales: {},
          valoresAnteriores: {},
          evalKeyToTernaId: {},
          asesoriaId: null,
          inicializado: false,
        });
      },

      initAnteriores: (anteriorMap) => {
        set({ valoresAnteriores: anteriorMap });
      },

      setCampo: (evalKey, campo, valor) => {
        set((state) => {
          const original = state.valoresOriginales[evalKey];
          const cambioActual = state.cambios[evalKey] ?? {};
          const esOriginal = esIgualCampo(valor, original?.[campo]);

          if (esOriginal) {
            const { [campo]: _, ...resto } = cambioActual;
            if (Object.keys(resto).length === 0) {
              const { [evalKey]: __, ...sinKey } = state.cambios;
              return { cambios: sinKey };
            }
            return { cambios: { ...state.cambios, [evalKey]: resto } };
          }

          return {
            cambios: {
              ...state.cambios,
              [evalKey]: { ...cambioActual, [campo]: valor },
            },
          };
        });
      },

      limpiarCambios: () => set({ cambios: {} }),

      limpiarCambiosEspecificos: (evalKeys) => {
        set((state) => {
          const nuevosCambios = { ...state.cambios };
          for (const key of evalKeys) delete nuevosCambios[key];
          return { cambios: nuevosCambios };
        });
      },

      getValor: (evalKey, campo) => {
        const state = get();
        return state.cambios[evalKey]?.[campo] ?? state.valoresOriginales[evalKey]?.[campo];
      },

      getValores: (evalKey) => {
        const state = get();
        const original = state.valoresOriginales[evalKey] ?? emptyCambio;
        const cambios = state.cambios[evalKey] ?? {};
        return { ...original, ...cambios };
      },
    }),
    {
      name: 'evaluacion-terna-draft',
      partialize: (state) => ({
        cambios: state.cambios,
        asesoriaId: state.asesoriaId,
      }),
    }
  ),
  Object.is
);

export const useEvaluacionTernaStore = store;
export { store as evaluacionTernaStore };

// ========================================
// Hooks
// ========================================

export function useCampoTerna<K extends keyof CambioTerna>(evalKey: string, campo: K): CambioTerna[K] | undefined {
  return useEvaluacionTernaStore((s) => {
    return s.cambios[evalKey]?.[campo] ?? s.valoresOriginales[evalKey]?.[campo];
  });
}

export function useTernaEvalValues(evalKey: string): CambioTerna {
  return useEvaluacionTernaStore((s) => {
    const original = s.valoresOriginales[evalKey] ?? emptyCambio;
    const cambios = s.cambios[evalKey] ?? {};
    return { ...original, ...cambios };
  });
}
